import { Injectable } from "@angular/core";
import { HttpHeaders, HttpClient } from "@angular/common/http";

import { Observable, throwError } from "rxjs";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/store/reducers";
import { catchError, map } from "rxjs/operators";

import { AppSessionService } from "../app-session/app-session.service";

import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { FilterOperation } from "src/app/models/custom-table/custom-table.model";
import { setAdvancedFilterQuery, setSimpleFilterQuery } from "../../utils/tableUtils";
import { GridUtils } from "../../utils/gridUtils";
import { TenantDTO } from "src/app/models/Tenant/Tenant.model";

const EMPTY_STRING = "";

@Injectable({
  providedIn: "root",
})
export class TenantService {  
  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private appSession: AppSessionService
  ) {}

  private BASE_URL = "/api/v1/tenants/current";
  private TENANTS_URL = "/api/v1/tenants";

  
  getTenant(code: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.appSession.baseUrl}${this.TENANTS_URL}/${code}`;
    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getTenants(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }
    const url = `${this.appSession.baseUrl}${this.BASE_URL}`;
    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 40,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getTenantsList(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    const url = paging
      ? this.buildDataUrl(paging, "?")
      : `${this.appSession.baseUrl}${this.TENANTS_URL}`;

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  createTenant(tenant: TenantDTO): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(tenant);
    const url = `${this.appSession.baseUrl}${this.TENANTS_URL}`;
    return this.http.post<any>(url, data, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  updateTenant(tenant: any, code: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(tenant);
    const url = `${this.appSession.baseUrl}${this.TENANTS_URL}/${code}`;
    return this.http.put<any>(url, data, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  updateCurrentTenant(tenant: any): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(tenant);
    const url = `${this.appSession.baseUrl}${this.BASE_URL}`;
    return this.http.put(url, data, { headers });
  }

  // Grid filter helpers block
  private buildDataUrl(pagingArgs: any, path: string): string {
    let baseQueryString = `${this.appSession.baseUrl}${this.TENANTS_URL}${path}`;
    let scrollingQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = GridUtils._buildScrollExpression(pagingArgs);
    }

    query += scrollingQuery !== EMPTY_STRING ? `&${scrollingQuery}` : EMPTY_STRING;
    baseQueryString += query;

    return baseQueryString;
  }
}
